import { useEffect, useState } from 'react'
import { PetType } from '@fitpet/utils/pet-types'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import {
  getGetProductsByDisplayCollectionIdV3QueryKey,
  getProductsByDisplayCollectionIdV3,
  useGetBestDisplayCollectionList,
} from '@/apis/rest/generated/hooks'
import { SearchProductInDisplayCollectionSortByEnumV2, SortDirectionEnum } from '@/apis/rest/generated/types'
import { legacySlugToBestTabName } from '@/constants/best-seller-category-type.enum'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { useCustomRouter, usePetType } from '@/containers/hooks'

const useBestSellerProductsListV3 = () => {
  const router = useCustomRouter()
  const { query } = useCustomRouter()
  const { petType: queryPetType } = query as { petType: PetType }
  const { getPetType, setPetType } = usePetType()
  const petType = queryPetType || getPetType()

  const { ref, inView } = useInView()
  const [selectedId, setSelectedId] = useState<number>()
  const { categoryName, orderBy } = query as { categoryName: string; orderBy: string }

  const {
    data: productListData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery(
    getGetProductsByDisplayCollectionIdV3QueryKey(selectedId!, {
      sortBy: SearchProductInDisplayCollectionSortByEnumV2.MD_RECOMMENDED,
      sortDirection: SortDirectionEnum.ASC,
    }),
    {
      queryFn: ({ pageParam = PAGE_LIMIT.DEFAULT_PAGE }) =>
        getProductsByDisplayCollectionIdV3(selectedId!, {
          page: pageParam,
          size: PAGE_LIMIT.HOME_BRAND,
          sortBy: SearchProductInDisplayCollectionSortByEnumV2.MD_RECOMMENDED,
          sortDirection: SortDirectionEnum.ASC,
          includeSoldOut: false,
        }),
      getNextPageParam: (lastPage, allPages) => {
        if (allPages.length === 5) {
          return
        }
        return lastPage.data?.products?.length === PAGE_LIMIT.HOME_BRAND
          ? allPages.length + PAGE_LIMIT.DEFAULT_PAGE
          : undefined
      },
      enabled: !!selectedId,
    }
  )

  const { data: categoryTabs } = useGetBestDisplayCollectionList(
    {
      petType,
    },
    {
      query: {
        onSuccess: (data) => {
          if (categoryName) {
            const target = data.find((item) => item.name === legacySlugToBestTabName(categoryName))
            setSelectedId(target?.id)
          }
        },
        enabled: !!router.isReady,
      },
    }
  )
  const changeTab = (id: number) => {
    setSelectedId(id)
  }

  // 카테고리 이름으로 카테고리 탭을 찾는 함수
  const findCategoryByName = (name: string) => {
    const categoryNameV4 = legacySlugToBestTabName(name)
    return categoryTabs?.find((item) => item.name === categoryNameV4)
  }

  useEffect(() => {
    if (!categoryName || !categoryTabs) {
      return
    }

    const targetCategory = findCategoryByName(categoryName)

    if (targetCategory?.id) {
      setSelectedId(targetCategory.id)
    }
  }, [categoryName, categoryTabs])

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  useEffect(() => {
    if (!queryPetType) {
      return
    }

    const { petType, ...rest } = query
    router.replace({
      pathname: router.pathname,
      query: rest,
    })

    setPetType(petType as PetType)
  }, [petType])

  return {
    ref,
    isLoading,
    productListData,
    orderBy,
    categoryTabs,
    selectedId,
    changeTab,
  }
}

export default useBestSellerProductsListV3
